import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Tag, Space, DatePicker, Select, message, Popconfirm, Card, Statistic, Row, Col, Input, Descriptions, Timeline, Divider, Badge } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, CloseCircleOutlined, DeleteOutlined, DownloadOutlined, CopyOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, StopOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';
import dayjs from 'dayjs';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

const { RangePicker } = DatePicker;

// 可调整宽度的表头组件
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          style={{
            position: 'absolute',
            right: -5,
            bottom: 0,
            width: 10,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]); // 用于导出
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchOrderNo, setSearchOrderNo] = useState('');  // 订单号搜索
  const [searchUserId, setSearchUserId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // 统计数据
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    succeeded: 0,
    failed: 0,
    cancelled: 0,
    totalAmount: 0,
  });

  // 列宽状态
  const [columns, setColumns] = useState([
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 180,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
    },
    {
      title: '货币',
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
    },
  ]);

  // 处理列宽调整
  const handleResize = (index) => (_, { size }) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: size.width,
    };
    setColumns(newColumns);
  };

  // 合并列宽调整功能到columns
  const mergedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  useEffect(() => {
    fetchOrders();
  }, [page, pageSize]);

  // 初始加载时获取全局统计数据
  useEffect(() => {
    fetchStats();
  }, []);

  // 搜索时只刷新订单列表，不更新统计
  useEffect(() => {
    if (page === 1) {
      fetchOrders();
    } else {
      setPage(1);
    }
  }, [searchStatus, searchUserId, dateRange, searchOrderNo]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchOrderNo) {
        params.order_no = searchOrderNo;
      }
      if (searchUserId) {
        params.user_id = searchUserId;
      }
      if (searchStatus) {
        params.status = searchStatus;
      }
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      // 获取当前页数据
      const response = await adminAPI.getOrders(page, pageSize, params);
      if (response.code === 0) {
        setOrders(response.data.list || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      message.error(t('fetch_orders_failed') || '获取订单列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取全局统计数据（不受筛选条件影响）
  const fetchStats = async () => {
    try {
      // 获取各状态的订单数量
      const [pendingRes, succeededRes, failedRes, cancelledRes] = await Promise.all([
        adminAPI.getOrders(1, 1, { status: 'pending' }),
        adminAPI.getOrders(1, 1, { status: 'succeeded' }),
        adminAPI.getOrders(1, 1, { status: 'failed' }),
        adminAPI.getOrders(1, 1, { status: 'cancelled' }),
      ]);
      
      // 获取成功订单来计算总金额
      const succeededTotal = succeededRes.data?.total || 0;
      let totalAmount = 0;
      if (succeededTotal > 0) {
        const succeededOrdersRes = await adminAPI.getOrders(1, Math.min(succeededTotal, 1000), { status: 'succeeded' });
        if (succeededOrdersRes.code === 0) {
          totalAmount = (succeededOrdersRes.data.list || []).reduce((sum, o) => sum + (o.amount || 0), 0);
        }
      }
      
      setStats({
        total: (pendingRes.data?.total || 0) + (succeededRes.data?.total || 0) + (failedRes.data?.total || 0) + (cancelledRes.data?.total || 0),
        pending: pendingRes.data?.total || 0,
        succeeded: succeededRes.data?.total || 0,
        failed: failedRes.data?.total || 0,
        cancelled: cancelledRes.data?.total || 0,
        totalAmount,
      });
    } catch (error) {
      console.error('获取统计数据失败', error);
    }
  };

  // 获取所有订单数据用于导出
  const fetchAllOrdersForExport = async () => {
    try {
      setExportLoading(true);
      const params = {};
      if (searchOrderNo) params.order_no = searchOrderNo;
      if (searchUserId) params.user_id = searchUserId;
      if (searchStatus) params.status = searchStatus;
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      
      // 获取所有数据（假设接口支持大页数）
      const response = await adminAPI.getOrders(1, 10000, params);
      if (response.code === 0) {
        setAllOrders(response.data.list || []);
        return response.data.list || [];
      }
      return [];
    } catch (error) {
      message.error(t('fetch_failed') || '获取数据失败');
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  // 导出为 CSV
  const exportToCSV = async () => {
    const orders = await fetchAllOrdersForExport();
    if (orders.length === 0) {
      message.warning(t('no_data_export') || '没有数据可导出');
      return;
    }

    const headers = ['订单号', '用户ID', '金额', '货币', '状态', 'Payment Intent ID', '创建时间', '完成时间'];
    const rows = orders.map(o => [
      o.order_no || o.id,
      o.user_id,
      o.amount,
      o.currency?.toUpperCase() || 'USD',
      o.status,
      o.stripe_payment_intent_id || '',
      dayjs(o.created_at).format('YYYY-MM-DD HH:mm:ss'),
      o.completed_at ? dayjs(o.completed_at).format('YYYY-MM-DD HH:mm:ss') : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success(t('export_success') || '导出成功');
  };

  // 导出为 Excel（简化版，使用 CSV）
  const handleExport = () => {
    Modal.confirm({
      title: t('confirm_export') || '确认导出',
      content: t('export_orders_tip') || '将导出当前筛选条件下的所有订单数据（CSV 格式）',
      onOk: exportToCSV,
    });
  };

  const handleSearch = () => {
    setPage(1);
    fetchOrders();
  };

  const handleReset = () => {
    setSearchOrderNo('');
    setSearchUserId('');
    setSearchStatus('');
    setDateRange(null);
    setPage(1);
    fetchOrders();
  };

  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setDetailModalVisible(true);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'processing', text: '处理中', icon: <ClockCircleOutlined /> },
      succeeded: { color: 'success', text: '成功', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', text: '失败', icon: <ExclamationCircleOutlined /> },
      cancelled: { color: 'default', text: '已取消', icon: <StopOutlined /> },
    };
    const config = statusMap[status] || { color: 'default', text: status, icon: null };
    return <Badge status={config.color} text={config.text} />;
  };

  // 取消订单
  const handleCancelOrder = async (order) => {
    try {
      const response = await adminAPI.cancelOrder(order.id);
      if (response.code === 0) {
        message.success('订单已取消');
        fetchOrders();
      } else {
        message.error(response.message || '取消失败');
      }
    } catch (error) {
      message.error('取消订单失败');
    }
  };

  // 删除订单
  const handleDeleteOrder = async (order) => {
    try {
      const response = await adminAPI.deleteOrder(order.id);
      if (response.code === 0) {
        message.success('订单已删除');
        fetchOrders();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除订单失败');
    }
  };

  // 复制到剪贴板
  const copyToClipboard = (text, label = '内容') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 列渲染函数
  const renderCell = (column, record) => {
    switch (column.key) {
      case 'order_no':
        return (
          <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
            {record.order_no || record.id}
          </span>
        );
      case 'user_id':
        return <span style={{ fontFamily: 'monospace' }}>{record.user_id}</span>;
      case 'amount':
        return (
          <span style={{ fontWeight: 600, color: '#52c41a' }}>
            ${record.amount} {record.currency?.toUpperCase() || 'USD'}
          </span>
        );
      case 'currency':
        return record.currency?.toUpperCase() || 'USD';
      case 'status':
        return getStatusTag(record.status);
      case 'created_at':
        return dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss');
      case 'completed_at':
        return record.completed_at ? dayjs(record.completed_at).format('YYYY-MM-DD HH:mm:ss') : '-';
      case 'action':
        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
        );
      default:
        return record[column.dataIndex];
    }
  };

  // 带渲染函数的列配置
  const columnsWithRender = mergedColumns.map(col => ({
    ...col,
    render: (_, record) => renderCell(col, record),
  }));

  return (
    <div className="fit-screen-page">
      {/* 功能区 */}
      <div className="page-header">
        <h2 className="page-title">{t('order_management') || '订单管理'}</h2>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total_orders') || '总订单'} value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('pending_orders') || '处理中'} value={stats.pending} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('succeeded_orders') || '成功'} value={stats.succeeded} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('failed_orders') || '失败'} value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('cancelled_orders') || '已取消'} value={stats.cancelled} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total_amount') || '总金额'} value={`$${stats.totalAmount.toFixed(2)}`} />
          </Card>
        </Col>
      </Row>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Input.Search
          placeholder="订单号"
          value={searchOrderNo}
          onChange={(e) => setSearchOrderNo(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 200 }}
          allowClear
          prefix={<span style={{ fontSize: 12, color: '#999' }}>#</span>}
        />
        <Input
          placeholder="用户ID"
          value={searchUserId}
          onChange={(e) => setSearchUserId(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 120 }}
          allowClear
        />
        <Select
          placeholder={t('order_status') || '订单状态'}
          value={searchStatus || undefined}
          onChange={(value) => { setSearchStatus(value); handleSearch(); }}
          style={{ width: 120 }}
          allowClear
        >
          <Select.Option value="pending">{t('pending') || '处理中'}</Select.Option>
          <Select.Option value="succeeded">{t('succeeded') || '成功'}</Select.Option>
          <Select.Option value="failed">{t('failed') || '失败'}</Select.Option>
          <Select.Option value="cancelled">{t('cancelled') || '已取消'}</Select.Option>
        </Select>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={setDateRange}
          style={{ width: 250 }}
          placeholder={['开始日期', '结束日期']}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          {t('search') || '搜索'}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          {t('reset') || '重置'}
        </Button>
        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exportLoading} style={{ marginLeft: 'auto' }}>
          {t('export') || '导出'}
        </Button>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Table
          columns={columnsWithRender}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content', y: 600 }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </div>

      {/* 订单详情对话框 - 专业设计 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>订单详情</span>
            {selectedOrder && (
              <Tag color={selectedOrder.status === 'succeeded' ? 'success' : selectedOrder.status === 'pending' ? 'processing' : selectedOrder.status === 'failed' ? 'error' : 'default'}>
                {selectedOrder.status === 'succeeded' ? '已支付' : selectedOrder.status === 'pending' ? '处理中' : selectedOrder.status === 'failed' ? '支付失败' : '已取消'}
              </Tag>
            )}
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          selectedOrder && selectedOrder.status === 'pending' ? (
            <Space>
              <Popconfirm
                title="确认取消此订单？"
                description="取消后订单将无法恢复"
                onConfirm={() => {
                  handleCancelOrder(selectedOrder);
                  setDetailModalVisible(false);
                }}
                okText="确认取消"
                cancelText="暂不"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<CloseCircleOutlined />}>取消订单</Button>
              </Popconfirm>
              <Popconfirm
                title="确认删除此订单？"
                description="删除后订单将无法恢复"
                onConfirm={() => {
                  handleDeleteOrder(selectedOrder);
                  setDetailModalVisible(false);
                }}
                okText="确认删除"
                cancelText="暂不"
                okButtonProps={{ danger: true }}
              >
                <Button danger type="text" icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            </Space>
          ) : null
        }
        width={720}
        centered
      >
        {selectedOrder && (
          <div style={{ padding: '8px 0' }}>
            {/* 订单金额展示 */}
            <div style={{ 
              textAlign: 'center', 
              padding: '24px 0', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              marginBottom: 24,
              color: '#fff'
            }}>
              <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>支付金额</div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>
                ${selectedOrder.amount}
                <span style={{ fontSize: 18, marginLeft: 4 }}>{selectedOrder.currency?.toUpperCase() || 'USD'}</span>
              </div>
            </div>

            {/* 基础信息 */}
            <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单号">
                <Space>
                  <span style={{ fontFamily: 'monospace' }}>{selectedOrder.order_no || selectedOrder.id}</span>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />} 
                    onClick={() => copyToClipboard(selectedOrder.order_no || selectedOrder.id, '订单号')}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="用户ID">
                <Space>
                  <span style={{ fontFamily: 'monospace' }}>{selectedOrder.user_id}</span>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />} 
                    onClick={() => copyToClipboard(selectedOrder.user_id, '用户ID')}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {getStatusTag(selectedOrder.status)}
              </Descriptions.Item>
              <Descriptions.Item label="支付方式">
                {selectedOrder.payment_method === 'stripe' ? 'Stripe' :
                 selectedOrder.payment_method === 'paypal' ? 'PayPal' :
                 selectedOrder.payment_method === 'fastspring' ? 'FastSpring' :
                 selectedOrder.payment_method || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '16px 0' }} />

            {/* 支付信息 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 12, fontWeight: 500 }}>支付信息</div>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Payment Intent ID">
                  <Space>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                      {selectedOrder.stripe_payment_intent_id || '-'}
                    </span>
                    {selectedOrder.stripe_payment_intent_id && (
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<CopyOutlined />} 
                        onClick={() => copyToClipboard(selectedOrder.stripe_payment_intent_id, 'Payment Intent ID')}
                      />
                    )}
                  </Space>
                </Descriptions.Item>
                {selectedOrder.stripe_event_id && (
                  <Descriptions.Item label="Stripe Event ID">
                    <Space>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                        {selectedOrder.stripe_event_id}
                      </span>
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<CopyOutlined />} 
                        onClick={() => copyToClipboard(selectedOrder.stripe_event_id, 'Event ID')}
                      />
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* 时间线 */}
            <div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 12, fontWeight: 500 }}>订单时间线</div>
              <Timeline
                items={[
                  {
                    color: 'blue',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>订单创建</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.created_at).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  },
                  ...(selectedOrder.status === 'succeeded' && selectedOrder.completed_at ? [{
                    color: 'green',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>支付完成</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.completed_at).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(selectedOrder.status === 'failed' ? [{
                    color: 'red',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>支付失败</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.updated_at || selectedOrder.created_at).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(selectedOrder.status === 'cancelled' ? [{
                    color: 'gray',
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>订单取消</div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          {dayjs(selectedOrder.updated_at || selectedOrder.created_at).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      </div>
                    ),
                  }] : []),
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
